import React from 'react';
import { pxToRem } from '@withjoy/joykit/theme';
import { StyledLinkV2 } from './Success.styles';
import { Confetti, Footer } from '../shared';
import { Card, Header } from '../Success/components';
import { ButtonV2, Flex, TextV2, useDisclosure } from '@withjoy/joykit';
import { Bed } from '@withjoy/joykit/icons';
import { useSuccessController } from './Success.controller';
import { GetDataForThanksForRSVPQuery } from '@graphql/generated';
import { NearbyHotelsData } from '@apps/thanksForRsvp/hooks/useNearbyHotelsData';
import { EmptyState } from './components/EmptyState/EmptyState';
import AccommodationsConsoleDialog from '@apps/accommodations';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { AccommodationsConsoleConfigProvider } from '@apps/accommodations/AccommodationsConsoleConfigContext';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useEventDisplayName } from '@shared/utils/eventInfo';
import { useTranslation } from '@shared/core';

interface SuccessProps
  extends Readonly<{
    eventHandle: string;
    queryData?: GetDataForThanksForRSVPQuery;
    fromGiftWrap: boolean;
    accommodationsCardVisibilityData: {
      isAccommodationsPageEnabled?: boolean;
    };
    nearbyHotelsData: NearbyHotelsData;
    showRegistryCard: boolean;
  }> {}

export const Success: React.FC<SuccessProps> = ({ eventHandle, queryData, fromGiftWrap, accommodationsCardVisibilityData, nearbyHotelsData, showRegistryCard }) => {
  const coupleNames = useEventDisplayName();
  const eventId = queryData?.eventByName?.id;

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [accommodationsConsoleOnThanksRsvpExperiment] = useIsFeatureEnabled('accommodationsConsoleOnThanksRsvpExperiment');
  const { tq } = useTranslation('thanksForRSVP');

  const { handleAccommodation, handleShopRegistry, handleBackToWebsite, message, showRoomBlocksCard, hasRoomBlocksBookingUrl } = useSuccessController(
    eventHandle,
    fromGiftWrap,
    !!queryData?.eventByName?.hotelRoomBlocks?.bookingLink,
    eventId ?? '',
    accommodationsCardVisibilityData,
    coupleNames,
    showRegistryCard,
  );

  const { shouldShowNearbyHotels, nearbyHotelsCopyText, nearbyHotelsCTALink, onClickTelemetry } = nearbyHotelsData;
  const showEmptyState = !showRoomBlocksCard && !showRegistryCard && !shouldShowNearbyHotels;

  const handleOnClickBrowseHotels = useEventCallback(() => {
    onClickTelemetry();
    onOpen();
  });

  return (
    <AccommodationsConsoleConfigProvider>
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        paddingY={[7, null, pxToRem(56)]}
        paddingX={[6, null, 0]}
      >
        <Flex
          minHeight="100%"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          width="100%"
          maxWidth={['100%', null, '540px']}
        >
          {!fromGiftWrap && <Confetti isSuccess />}
          <Header
            queryData={queryData}
            message={message}
            haveLinkOrRegistry={showRoomBlocksCard || showRegistryCard}
          />
          {(showRoomBlocksCard || shouldShowNearbyHotels || showRegistryCard) && (
            <Flex
              flexDirection="column"
              rowGap={6}
              alignItems="center"
              marginBottom={7}
            >
              <Flex
                flexDirection="column"
                rowGap={7}
              >
                {shouldShowNearbyHotels && (
                  <Card>
                    <TextV2 typographyVariant="display3">
                      <span
                        role="img"
                        aria-label="Hotels"
                      >
                        🏨
                      </span>
                    </TextV2>
                    <TextV2
                      typographyVariant="hed3"
                      marginTop={4}
                    >
                      {nearbyHotelsCopyText.heading}
                    </TextV2>
                    <TextV2
                      typographyVariant="body2"
                      marginTop={1}
                    >
                      {nearbyHotelsCopyText.subHeading}
                    </TextV2>
                    {accommodationsConsoleOnThanksRsvpExperiment ? (
                      <ButtonV2
                        shape="rounded"
                        intent="neutral"
                        variant="solid"
                        width="100%"
                        size="lg"
                        marginTop={7}
                        onClick={handleOnClickBrowseHotels}
                      >
                        {nearbyHotelsCopyText.ctaText}
                      </ButtonV2>
                    ) : (
                      <ButtonV2
                        shape="rounded"
                        intent="neutral"
                        variant="solid"
                        width="100%"
                        size="lg"
                        marginTop={7}
                        as="a"
                        href={nearbyHotelsCTALink}
                        onClick={onClickTelemetry}
                      >
                        {nearbyHotelsCopyText.ctaText}
                      </ButtonV2>
                    )}
                  </Card>
                )}
                {showRoomBlocksCard && (
                  <Card>
                    <Bed size="xl" />
                    <TextV2
                      typographyVariant="hed3"
                      marginTop={4}
                    >
                      {hasRoomBlocksBookingUrl ? tq`We've secured discounted rates on a hotel near our venue.` : tq`Where To Stay`}
                    </TextV2>
                    <TextV2
                      typographyVariant="body2"
                      marginTop={1}
                    >
                      {hasRoomBlocksBookingUrl
                        ? tq`Book soon while rooms are available.`
                        : tq`We have listed our recommended hotel options for your convenience. Book soon while rooms are available.`}
                    </TextV2>
                    <ButtonV2
                      intent="neutral"
                      variant="solid"
                      width="100%"
                      size="lg"
                      marginTop={7}
                      onClick={handleAccommodation}
                    >
                      {tq`View Accommodations`}
                    </ButtonV2>
                  </Card>
                )}
                {showRegistryCard && (
                  <Card>
                    <TextV2 typographyVariant="display3">
                      <span
                        role="img"
                        aria-label="Registry"
                      >
                        🎁
                      </span>
                    </TextV2>
                    <TextV2
                      typographyVariant="hed3"
                      marginTop={4}
                    >
                      {tq`Browse Registry`}
                    </TextV2>
                    <TextV2
                      typographyVariant="body2"
                      marginTop={1}
                    >
                      {coupleNames ? tq`Explore gifts ${coupleNames} added to their registry.` : tq`Explore gifts the host added to their registry.`}
                    </TextV2>
                    <ButtonV2
                      shape="rounded"
                      intent="neutral"
                      variant="outline"
                      width="100%"
                      size="lg"
                      marginTop={7}
                      onClick={handleShopRegistry}
                    >
                      {tq`View Registry`}
                    </ButtonV2>
                  </Card>
                )}
              </Flex>
            </Flex>
          )}
          {showEmptyState ? (
            <EmptyState
              handleBackToWebsite={handleBackToWebsite}
              title={tq`Event Details`}
              description={tq`Head back to the event website to find all the details.`}
              ctaText={tq`Back to Website`}
            />
          ) : (
            <StyledLinkV2
              color="mono14"
              onClick={handleBackToWebsite}
              _hover={{ color: 'mono8' }}
              textDecoration={showRoomBlocksCard || showRegistryCard ? 'none' : 'underline'}
              typographyVariant="button1"
              marginBottom={7}
            >
              {tq`Back to Website`}
            </StyledLinkV2>
          )}
          <Footer />
        </Flex>
      </Flex>
      <AccommodationsConsoleDialog
        isOpen={isOpen}
        onClose={onClose}
        eventHandle={eventHandle}
        eventName={coupleNames}
      />
    </AccommodationsConsoleConfigProvider>
  );
};
